import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Loader } from 'lucide-react';
import { employeesApi } from '../services/api';
import { message } from 'antd';
import { useTheme } from '../context/ThemeContext';

function EmployeeDetail() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.textColor === '#FFFFFF' || currentTheme?.textColor?.toLowerCase() === '#ffffff';
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmployeeDetails();
  }, [id]);

  const fetchEmployeeDetails = async () => {
    try {
      setLoading(true);
      const data = await employeesApi.getEmployeeById(id);
      console.log("data", data)
      setEmployee(data);
    } catch (error) {
      console.error('Error fetching employee details:', error);
      message.error('Failed to load employee details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>
        <Loader className="w-8 h-8 animate-spin" style={{ color: currentTheme.color }} />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className={`flex items-center justify-center min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>
        <div className="text-center">
          <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>Employee not found</h3>
          <button
            onClick={() => navigate('/employees')}
            className="font-medium hover:underline"
            style={{ color: currentTheme.color }}
          >
            Go back to employees
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-4 desktop:p-8 space-y-6 transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/employees')}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
          >
            <ArrowLeft className={`w-6 h-6 ${isDark ? 'text-white' : 'text-gray-600'}`} />
          </button>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: currentTheme.color }}>
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{employee.employeeId}</h2>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {/* Basic Details */}
        <div className={`rounded-xl p-6 border shadow-sm transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Basic Details</h3>

          <div className="space-y-4">
            <div>
              <label className={`text-sm block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Employee Name</label>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{employee.name || 'N/A'}</p>
            </div>

            <div>
              <label className={`text-sm block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Contact number</label>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{employee.contactNumber || 'N/A'}</p>
            </div>

            <div>
              <label className={`text-sm block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email</label>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{employee.email || 'N/A'}</p>
            </div>

            <div>
              <label className={`text-sm block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Gender</label>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {employee.gender === 'MALE' ? 'Male' : employee.gender === 'FEMALE' ? 'Female' : employee.gender || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className={`rounded-xl p-6 border shadow-sm transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Address</h3>

          <div className="space-y-2">
            <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
              {employee.addressStreet && `${employee.addressStreet}, `}
              {employee.landmark && `${employee.landmark}, `}
              {employee.city && `${employee.city}, `}
              {employee.district && `${employee.district}, `}
              {employee.state && `${employee.state}, `}
              {employee.pincode && `${employee.pincode}`}
            </p>
            {!employee.addressStreet && !employee.city && !employee.state && (
              <p className={`text-sm ${isDark ? 'text-gray-600' : 'text-gray-500'}`}>No address provided</p>
            )}
          </div>
        </div>

        {/* Password */}
        <div className={`rounded-xl p-6 border shadow-sm transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Password</h3>

          <div className="space-y-4">
            <div>
              <label className={`text-sm block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Password</label>
              <div className={`px-4 py-3 rounded-lg border transition-all ${isDark ? 'bg-[#0f172a] border-white/10' : 'bg-gray-100 border-gray-200'}`}>
                <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{employee.password || employee.plainPassword || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Employment Details */}
      <div className={`rounded-xl p-6 border shadow-sm transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Employment details</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <div>
            <label className={`text-sm block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Department</label>
            <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{employee.departmentName || 'N/A'}</p>
          </div>

          <div>
            <label className={`text-sm block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Team</label>
            <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{employee.teamName || 'N/A'}</p>
          </div>

          <div>
            <label className={`text-sm block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Designation</label>
            <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{employee.designationName || 'N/A'}</p>
          </div>

          <div>
            <label className={`text-sm block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Employee Code</label>
            <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{employee.employeeCode || 'N/A'}</p>
          </div>

          <div>
            <label className={`text-sm block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Agent ID</label>
            <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{employee.agentId || 'N/A'}</p>
          </div>

          <div>
            <label className={`text-sm block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Employee ID</label>
            <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{employee.employeeId || 'N/A'}</p>
          </div>

          <div>
            <label className={`text-sm block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Date of Joining</label>
            <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{employee.dateOfJoining ? new Date(employee.dateOfJoining).toLocaleDateString() : 'N/A'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployeeDetail;
